import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Clock, ChevronDown } from 'lucide-react';
import { useAppSelector } from '../../store/hooks';

interface TimePickerProps {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  error?: string;
  helpText?: string;
  disabled?: boolean;
  className?: string;
  required?: boolean;
  minTime?: string; // Minimum time in HH:MM format (e.g., "14:30")
}

const TimePicker: React.FC<TimePickerProps> = ({
  value,
  onChange,
  placeholder = 'Select time',
  label,
  error,
  helpText,
  disabled = false,
  className = '',
  required = false,
  minTime
}) => {
  const { theme } = useAppSelector((state) => state.ui);
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [hours, setHours] = useState(12);
  const [minutes, setMinutes] = useState(0);
  const [dropdownCoords, setDropdownCoords] = useState({ top: 0, left: 0, width: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLDivElement>(null);

  // Parse the current value
  useEffect(() => {
    if (value) {
      const [timeHours, timeMinutes] = value.split(':').map(Number);
      setHours(timeHours);
      setMinutes(timeMinutes);
    }
  }, [value]);

  // Calculate dropdown position based on viewport space
  const calculateDropdownPosition = () => {
    if (!inputRef.current) {
      setDropdownCoords({ top: 0, left: 0, width: 0 });
      return 'below';
    }
    
    const rect = inputRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const dropdownHeight = 300; // Approximate height of the time picker dropdown
    const dropdownWidth = 320; // Approximate width of the time picker dropdown
    
    // Check if there's enough space below
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    
    // Determine if dropdown should appear above or below
    const position = spaceBelow < dropdownHeight && spaceAbove > spaceBelow ? 'above' : 'below';
    
    // Calculate top position
    let top = 0;
    if (position === 'below') {
      top = rect.bottom + 4; // 4px gap (mt-1 equivalent)
    } else {
      top = rect.top - dropdownHeight - 4; // 4px gap (mb-1 equivalent)
    }
    
    // Calculate left position (align with input, but ensure it stays within viewport)
    let left = rect.left;
    if (left + dropdownWidth > viewportWidth) {
      left = viewportWidth - dropdownWidth - 8; // 8px margin from edge
    }
    if (left < 8) {
      left = 8; // 8px margin from edge
    }
    
    setDropdownCoords({
      top: Math.max(8, Math.min(top, viewportHeight - dropdownHeight - 8)), // Ensure it stays within viewport
      left,
      width: rect.width
    });
    
    return position;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;
    
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        containerRef.current && 
        !containerRef.current.contains(target) &&
        !(target instanceof Element && target.closest('[data-timepicker-dropdown]'))
      ) {
        setIsOpen(false);
        setIsFocused(false);
      }
    };

    // Use a slight delay to avoid closing immediately when opening
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Update dropdown position when opening
  useEffect(() => {
    if (isOpen) {
      calculateDropdownPosition();
    }
  }, [isOpen]);

  // Recalculate position on window resize and scroll
  useEffect(() => {
    if (!isOpen) return;

    const handleResize = () => {
      calculateDropdownPosition();
    };

    const handleScroll = () => {
      calculateDropdownPosition();
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, true); // Use capture phase to catch all scroll events

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  // Generate quick time presets
  const timePresets = [
    '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00'
  ];

  // Check if a time is valid (not before minTime)
  const isTimeValid = (timeHours: number, timeMinutes: number): boolean => {
    if (!minTime) return true;
    const [minHours, minMinutes] = minTime.split(':').map(Number);
    const timeInMinutes = timeHours * 60 + timeMinutes;
    const minTimeInMinutes = minHours * 60 + minMinutes;
    return timeInMinutes >= minTimeInMinutes;
  };

  const handlePresetClick = (preset: string) => {
    const [presetHours, presetMinutes] = preset.split(':').map(Number);
    if (!isTimeValid(presetHours, presetMinutes)) {
      return; // Don't allow selecting past times
    }
    setHours(presetHours);
    setMinutes(presetMinutes);
    onChange(preset);
    setIsOpen(false);
    setIsFocused(false);
  };

  const handleTimeChange = () => {
    if (!isTimeValid(hours, minutes)) {
      return; // Don't allow setting past times
    }
    const timeString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    onChange(timeString);
    setIsOpen(false);
    setIsFocused(false);
  };

  const formatDisplayValue = () => {
    if (!value) return placeholder;
    
    const [timeHours, timeMinutes] = value.split(':').map(Number);
    return `${timeHours.toString().padStart(2, '0')}:${timeMinutes.toString().padStart(2, '0')}`;
  };

  const formatTimeDisplay = (h: number, m: number) => {
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  };

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {/* Label */}
      {label && (
        <label className={`block text-sm font-medium mb-2 ${
          theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
        }`}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Input Button */}
      <div
        ref={inputRef}
        className={`relative cursor-pointer ${
          disabled ? 'opacity-50 cursor-not-allowed' : ''
        }`}
        onClick={() => {
          if (!disabled) {
            setIsOpen(!isOpen);
            setIsFocused(!isOpen);
          }
        }}
        onFocus={() => !disabled && setIsFocused(true)}
        onBlur={() => {
          // Delay blur to allow click events to process
          setTimeout(() => {
            if (!isOpen) {
              setIsFocused(false);
            }
          }, 200);
        }}
        tabIndex={disabled ? -1 : 0}
        role="button"
        aria-expanded={isOpen}
        aria-haspopup="dialog"
      >
        <div className={`w-full px-3 py-2 border rounded-md shadow-sm transition-all ${
          isFocused || isOpen
            ? 'ring-2 ring-mirsal-green ring-opacity-20 border-mirsal-green'
            : ''
        } ${
          theme === 'dark' 
            ? 'border-gray-600 bg-gray-800 text-white' 
            : 'border-gray-300 bg-white text-gray-900'
        } ${error ? 'border-red-500' : ''}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Clock className={`h-4 w-4 mr-2 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`} />
              <span className={`${
                !value ? theme === 'dark' ? 'text-gray-400' : 'text-gray-500' : ''
              }`}>
                {formatDisplayValue()}
              </span>
            </div>
            <ChevronDown className={`h-4 w-4 transition-transform ${
              isOpen ? 'rotate-180' : ''
            } ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
          </div>
        </div>
      </div>

      {/* Time Picker Dropdown - Rendered via Portal */}
      {isOpen && typeof document !== 'undefined' && createPortal(
        <div 
          data-timepicker-dropdown
          className={`fixed z-[9999] rounded-md shadow-lg ring-1 ring-black ring-opacity-5 ${
            theme === 'dark' 
              ? 'bg-gray-800 border border-gray-700' 
              : 'bg-white border border-gray-200'
          }`}
          style={{
            top: `${dropdownCoords.top}px`,
            left: `${dropdownCoords.left}px`,
            width: `${dropdownCoords.width || 320}px`,
            minWidth: '280px'
          }}
        >
          <div className="p-4">
            {/* Quick Presets */}
            <div className="mb-4">
              <div className={`text-xs font-medium mb-2 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                Quick Times
              </div>
              <div className="grid grid-cols-4 gap-2">
                {timePresets.map((preset) => {
                  const [presetHours, presetMinutes] = preset.split(':').map(Number);
                  const isValid = isTimeValid(presetHours, presetMinutes);
                  return (
                    <button
                      key={preset}
                      onClick={() => handlePresetClick(preset)}
                      disabled={!isValid}
                      className={`px-2 py-1 text-xs rounded transition-colors ${
                        !isValid
                          ? 'opacity-50 cursor-not-allowed bg-gray-300 text-gray-500'
                          : value === preset
                          ? 'bg-mirsal-green text-white'
                          : theme === 'dark'
                          ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {preset}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Manual Time Input */}
            <div className="mb-4">
              <div className={`text-xs font-medium mb-2 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                Custom Time
              </div>
              <div className="flex items-center space-x-2">
                <div className="flex items-center space-x-1">
                  <input
                    type="number"
                    min={minTime ? minTime.split(':')[0] : "0"}
                    max="23"
                    value={hours}
                    onChange={(e) => {
                      const newHours = Math.max(0, Math.min(23, parseInt(e.target.value) || 0));
                      // If minTime is set and we're on the same day, ensure hours are valid
                      if (minTime) {
                        const [minHours] = minTime.split(':').map(Number);
                        if (newHours < minHours) {
                          return; // Don't allow hours before minTime
                        }
                        // If hours equal minHours, ensure minutes are valid
                        if (newHours === minHours && minutes < parseInt(minTime.split(':')[1])) {
                          setMinutes(parseInt(minTime.split(':')[1]));
                        }
                      }
                      setHours(newHours);
                    }}
                    className={`w-16 px-3 py-1 text-sm border rounded ${
                      theme === 'dark'
                        ? 'border-gray-600 bg-gray-700 text-white'
                        : 'border-gray-300 bg-white text-gray-900'
                    }`}
                  />
                  <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>:</span>
                  <input
                    type="number"
                    min={minTime && hours === parseInt(minTime.split(':')[0]) ? minTime.split(':')[1] : "0"}
                    max="59"
                    step="5"
                    value={minutes}
                    onChange={(e) => {
                      const newMinutes = Math.max(0, Math.min(59, parseInt(e.target.value) || 0));
                      // If minTime is set and we're on the same day and hour, ensure minutes are valid
                      if (minTime) {
                        const [minHours, minMinutes] = minTime.split(':').map(Number);
                        if (hours === minHours && newMinutes < minMinutes) {
                          return; // Don't allow minutes before minTime
                        }
                      }
                      setMinutes(newMinutes);
                    }}
                    className={`w-16 px-3 py-1 text-sm border rounded ${
                      theme === 'dark'
                        ? 'border-gray-600 bg-gray-700 text-white'
                        : 'border-gray-300 bg-white text-gray-900'
                    }`}
                  />
                </div>
                <div className={`text-sm font-mono ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  {formatTimeDisplay(hours, minutes)}
                </div>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex justify-end space-x-2 pt-3 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setIsOpen(false)}
                className={`px-3 py-1 text-sm rounded-md transition-colors ${
                  theme === 'dark'
                    ? 'text-gray-300 hover:bg-gray-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleTimeChange}
                className="px-3 py-1 text-sm bg-mirsal-green text-white rounded-md hover:bg-mirsal-green/90 transition-colors"
              >
                Set Time
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Error Message */}
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}

      {/* Help Text */}
      {helpText && !error && (
        <p className={`mt-1 text-xs ${
          theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
        }`}>
          {helpText}
        </p>
      )}
    </div>
  );
};

export default TimePicker;